import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Truck, FlaskConical, CheckCircle2, AlertCircle, CircleDashed, XCircle, Circle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fmt } from '@/lib/format';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, type Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Toolbar } from '@/components/common/Toolbar';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { dispatchService } from '@/services/dispatch';
import type { DispatchPartner, PartnerConnectionStatus } from '@/types/dispatch';
import { useDebounced } from '@/hooks/useDebounced';
import { PartnerOnboardingWizard } from './PartnerOnboardingWizard';

/**
 * D-1 — Delivery Partners CRUD. The BE controller has existed since
 * F2 (connector marketplace); this is the FE surface so an operator
 * can author partners without writing SQL. MANUAL = the local guy on
 * a bike (operator fills tracking number by hand); COURIER = an
 * integrated carrier (Shiprocket / Delhivery / BlueDart) whose API
 * fetches AWBs automatically once Bundle C lands.
 */
export function DispatchPartnersPage({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DispatchPartner | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<DispatchPartner | null>(null);
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dispatch', 'partners'],
    queryFn: () => dispatchService.listPartners(),
  });

  // Delete a partner — the BE hard-deletes when the partner has no
  // dispatch/routing history, otherwise soft-deletes (status ARCHIVED) so
  // historical dispatches still resolve it. We surface which happened.
  const removePartner = useMutation({
    mutationFn: (id: string) => dispatchService.deletePartner(id),
    onSuccess: (res) => {
      toast.success(
        res.archived
          ? 'Partner archived (it has dispatch history, so it was kept for reference).'
          : 'Partner deleted.',
      );
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not delete partner'),
  });

  // Sh-mockpartner — one-click test setup. Creates a MANUAL partner
  // called "Mock Carrier" + runs testConnection so the row lands in
  // CONNECTED. The operator can then build an assignment rule that
  // routes everything to this partner, and exercise the full dispatch
  // flow (auto-assign on payment → label → pack & verify → handover)
  // without depending on a real courier integration.
  const quickAddMock = useMutation({
    mutationFn: async () => {
      const stamp = Math.random().toString(36).slice(2, 6).toUpperCase();
      const partner = await dispatchService.createPartner({
        provider: 'MANUAL',
        code: `MOCK-${stamp}`,
        name: 'Mock Carrier',
        contactName: 'Mock Dispatcher',
        contactEmail: 'mock@example.test',
        contactPhone: '+91 0000 000 000',
      });
      // MANUAL adapter's testConnection trivially returns ok — flips
      // the status to CONNECTED so it's eligible for assignment rules
      // straight away.
      try {
        await dispatchService.testPartner(partner.id, {});
      } catch {
        // Don't fail the quick-add if testConnection had a transient
        // hiccup — the partner row exists and can be re-tested
        // manually from the wizard.
      }
      return partner;
    },
    onSuccess: (partner) => {
      toast.success(`Mock partner "${partner.code}" added — ready for test routing.`);
      qc.invalidateQueries({ queryKey: ['dispatch', 'partners'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not add mock partner'),
  });

  const filtered = useMemo(() => {
    const rows = data ?? [];
    const q = debounced.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (p) =>
        p.code.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.contactName?.toLowerCase().includes(q) ?? false),
    );
  }, [data, debounced]);

  const columns: Column<DispatchPartner>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (p) => <span className="font-mono text-xs text-foreground">{p.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      cell: (p) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{p.name}</div>
          {p.accountInfo && (
            <div className="text-[11px] text-success" title="Account this partner is authenticated as, captured from the last successful test.">
              Connected as {p.accountInfo}
            </div>
          )}
          {p.contactName && (
            <div className="text-xs text-muted-foreground">
              {p.contactName}
              {p.contactPhone && <> · {p.contactPhone}</>}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      cell: (p) => (
        <Badge variant={p.provider === 'MANUAL' ? 'muted' : 'success'} className="text-[10px] font-mono">
          {p.provider}
        </Badge>
      ),
    },
    {
      key: 'connection',
      header: 'Connection',
      cell: (p) => <ConnectionPill status={p.connectionStatus} error={p.lastTestError} />,
    },
    {
      key: 'webhook',
      header: 'Webhook',
      width: '180px',
      cell: (p) => <WebhookHealthCell partner={p} />,
    },
    { key: 'status', header: 'Status', cell: (p) => <StatusBadge status={p.status} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (p) => (
        <div className="-mr-2 flex justify-end gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(p)}
            aria-label={`Edit ${p.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmRemove(p)}
            className="text-muted-foreground hover:text-destructive"
            aria-label={`Delete ${p.name}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  const headerActions = (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => quickAddMock.mutate()}
        disabled={quickAddMock.isPending}
        title="Spin up a MANUAL partner so you can test the dispatch flow without a real carrier integration."
      >
        <FlaskConical className="h-3.5 w-3.5" />
        {quickAddMock.isPending ? 'Adding…' : 'Add mock partner'}
      </Button>
      <Button size="sm" onClick={() => setCreateOpen(true)}>
        <Plus className="h-3.5 w-3.5" />
        New partner
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      {embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-2xl text-[11.5px] text-muted-foreground">
            Couriers and manual partners the dispatch automation can assign. Add one per courier
            (Shiprocket, BlueDart, …) or a MANUAL partner for in-house / local-courier dispatch.
          </p>
          {headerActions}
        </div>
      ) : (
        <PageHeader
          eyebrow="Fulfilment"
          title="Delivery Partners"
          description="Couriers and manual partners the dispatch automation can assign. Add one per courier (Shiprocket, BlueDart, …) or one MANUAL partner for in-house / local-courier dispatch."
          actions={headerActions}
        />
      )}

      <Toolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search code, name, contact…"
      />

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(p) => p.id}
          isLoading={isLoading}
          emptyTitle="No delivery partners yet"
          emptyDescription="Add a partner so the routing rules engine has something to assign."
          emptyAction={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Truck className="h-3.5 w-3.5" />
              New partner
            </Button>
          }
        />
      )}

      <PartnerOnboardingWizard open={createOpen} onOpenChange={setCreateOpen} />
      <PartnerOnboardingWizard
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        existingPartner={editing}
      />

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={confirmRemove ? `Delete partner "${confirmRemove.name}"?` : ''}
        description={
          <>
            Removes this delivery partner. If it has no dispatch or routing
            history it's deleted outright; otherwise it's archived (hidden from
            the list) so past dispatches still resolve it. Assignment rules
            pointing at it stop matching.
          </>
        }
        confirmLabel="Delete partner"
        tone="destructive"
        onConfirm={async () => {
          if (confirmRemove) await removePartner.mutateAsync(confirmRemove.id);
        }}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────

function ConnectionPill({
  status,
  error,
}: {
  status: PartnerConnectionStatus;
  error: string | null | undefined;
}) {
  const meta: Record<PartnerConnectionStatus, { icon: JSX.Element; label: string; cls: string }> = {
    CONNECTED:      { icon: <CheckCircle2 className="h-3 w-3" />, label: 'Connected',     cls: 'bg-success/10 text-success' },
    NOT_CONFIGURED: { icon: <CircleDashed className="h-3 w-3" />, label: 'Needs setup',    cls: 'bg-muted text-muted-foreground' },
    AUTH_FAILED:    { icon: <AlertCircle className="h-3 w-3" />,  label: 'Auth failed',    cls: 'bg-destructive/10 text-destructive' },
    SUSPENDED:      { icon: <AlertCircle className="h-3 w-3" />,  label: 'Suspended',      cls: 'bg-warning/10 text-warning-foreground' },
  };
  const m = meta[status];
  return (
    <div className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10.5px] ${m.cls}`} title={error ?? undefined}>
      {m.icon}
      {m.label}
    </div>
  );
}

/**
 * Compact webhook-health indicator on the partners list. Reads
 * the per-partner summary columns the BE ingress updates on every
 * inbound hit — no extra fetch needed. Links into the tenant's
 * Inbound webhooks page filtered to this partner.
 *
 * - Manual partners don't take webhooks: show a dash.
 * - Never received: grey dot + "Not yet".
 * - Last accepted: green dot + relative time.
 * - Last rejected: red dot + last error (truncated, tooltip with full).
 */
function WebhookHealthCell({ partner }: { partner: DispatchPartner }) {
  if (partner.provider === 'MANUAL') {
    return <span className="text-[11.5px] text-muted-foreground">—</span>;
  }
  const lastAt = partner.lastWebhookAt;
  if (!lastAt) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground"
        title="No inbound webhook from this partner yet."
      >
        <Circle className="h-2 w-2 fill-current opacity-40" />
        Not yet
      </span>
    );
  }
  const isAccepted = partner.lastWebhookStatus === 'ACCEPTED';
  return (
    <Link
      to={`/platform/webhooks/inbound?sourceId=${partner.id}`}
      className="inline-flex items-center gap-1 text-[11.5px] hover:underline"
      title={
        isAccepted
          ? `Last accepted at ${fmt.dateTime(lastAt)}`
          : (partner.lastWebhookError ?? 'Rejected')
      }
    >
      {isAccepted ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      ) : (
        <XCircle className="h-3 w-3 text-destructive" />
      )}
      <span className={isAccepted ? 'text-foreground' : 'text-destructive'}>
        {fmt.relative(lastAt)}
      </span>
    </Link>
  );
}

